<template>
	<AppLayout
		:title="trans('rotations.periods.slots.title')"
		:subtitle="rotationPeriod.title"
		:return-url="route('association.representatives.periods.index')"
		:return-label="trans('rotations.periods.title')"
	>
		<Modal :id="modalId">
			<template #title>{{ trans('rotations.periods.slots.upload') }}</template>
			<LabeledObject>
				<template #label>{{ trans('rotations.periods.slots.file') }}</template>
				<input type="file" @change.stop="loadFile($event)">
			</LabeledObject>
			<template #footer>
				<PrimaryButton :color="Color.Green" @click.stop="updateSlots">
					<i class="fa-solid fa-check mr-2"/>
					{{ trans('general.confirm') }}
				</PrimaryButton>
			</template>
		</Modal>
		<template #actions>
			<a :href="route('association.representatives.periods.slots.show')">
				<SecondaryButton :color="Color.Blue">
					<i class="fa-solid fa-download mr-2"/>
					{{ trans('general.sample') }}
				</SecondaryButton>
			</a>
			<PrimaryButton :color="Color.Green" @click.stop="showModal(modalId)">
				<i class="fa-solid fa-upload mr-2"/>
				{{ trans('general.upload_file') }}
			</PrimaryButton>
		</template>
		<Empty v-if="slots.length === 0"/>
		<CardGrid v-else>
			<Card v-for="(dateSlots, date) in slots" :title="dayjs(date).format(trans('general.dayjs.date'))">
				<List>
					<ListItem v-for="slot in dateSlots" :key="`period-slot-${slot.id}`" :title="slot.title">
						<Badge v-if="slot.taken" :color="Color.Red" active>{{ trans('rotations.periods.slots.taken') }}</Badge>
						<Badge v-else :color="Color.Green">{{ trans('rotations.periods.slots.free') }}</Badge>
					</ListItem>
				</List>
			</Card>
		</CardGrid>
	</AppLayout>
</template>
<script setup lang="ts">
import axios from 'axios'
import dayjs from 'dayjs'
import {route} from 'ziggy-js'
import {trans} from 'laravel-vue-i18n'
import {Color} from '@/Types/Enums/Color'
import Empty from '@/Components/UI/Empty.vue'
import List from '@/Components/UI/Lists/List.vue'
import Card from '@/Components/UI/Cards/Card.vue'
import {defineProps, type Ref, ref} from 'vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import Badge from '@/Components/UI/Badges/Badge.vue'
import Modal from '@/Components/UI/Modals/Modal.vue'
import ListItem from '@/Components/UI/Lists/ListItem.vue'
import CardGrid from '@/Components/UI/Cards/CardGrid.vue'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import SecondaryButton from '@/Components/UI/Buttons/SecondaryButton.vue'
import {type RotationSlot} from '@/Types/Periods/Rotations/RotationSlot'
import {type RotationPeriod} from '@/Types/Periods/Rotations/RotationPeriod'
import {closeModal, showError, showInfo, showModal, showSuccess, showValidationErrors} from '@/Helpers/general'

const props = defineProps<{
	slots:Array<Array<RotationSlot>>,
	rotationPeriod:RotationPeriod,
}>()

const modalId:Ref<string> = ref('update-slots-modal')
const updating:Ref<boolean> = ref(false)
const slotFile:Ref<Blob|null> = ref(null)
const slots:Ref<Array<Array<RotationSlot>>> = ref(props.slots)
const rotationPeriod:Ref<RotationPeriod> = ref(props.rotationPeriod)

function loadFile(event:Event):void{
	if(event.target && event.target instanceof HTMLInputElement && event.target.files){
		slotFile.value = event.target.files[0]
	}
}

function updateSlots():void{
	if(!updating.value && slotFile.value){
		updating.value = true
		showInfo(trans('general.updating'))
		const formData = new FormData
		formData.append('slots', slotFile.value)
		axios.post(route('association.representatives.periods.slots.update', rotationPeriod.value.id), formData, {
			headers: {
				'Content-Type': 'multipart/form-data',
			}
		}).then(({data}) => {
			showSuccess(trans('general.updated'))
			closeModal(modalId.value)
			slots.value = data
		}).catch(showValidationErrors).finally(() => {
			updating.value = false
		})
	} else if(!slotFile.value){
		showError(trans('general.file_missing'))
	}
}
</script>